import { useCallback, useEffect, useEffectEvent, useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import useDateText, { DateTextVariants } from '@/components/DateText/useDateText';
import { pastExportsOptions } from '@/features/DataHub/api';
import {
  createUniqueExportName,
  createUniqueExportNameFromBaseName
} from '@/features/DataHub/modules/Export/services/ExportNameService';
import { useAppSelector } from '@/hooks/redux-hooks';

const useExportName = () => {
  const [exportName, setExportName] = useState<string | null>(null);
  const { formatDateString } = useDateText();
  const { data: pastExports = [] } = useQuery(pastExportsOptions.list());
  const exportNameBase = useAppSelector(state => state.export.exportNameBase);

  const createExportName = useCallback((): string => {
    const normalizedExportNameBase = exportNameBase?.trim();
    const existingExports = pastExports.map(e => ({ fileName: e.name }));

    if (normalizedExportNameBase) {
      return createUniqueExportNameFromBaseName(normalizedExportNameBase, existingExports);
    }

    return createUniqueExportName(existingExports, (isoDate: string) =>
      formatDateString(isoDate, DateTextVariants.FULL)
    );
  }, [exportNameBase, pastExports, formatDateString]);

  const initExportName = useEffectEvent(() => {
    if (exportName !== null) return;
    setExportName(createExportName());
  });

  useEffect(() => {
    initExportName();
  }, [createExportName]);

  return { exportName, setExportName, createExportName };
};

export default useExportName;
